import { useEffect, useState } from 'react';
import { useBreadcrumbContext } from '../../../../../domain/utility/AppContextProvider/BreadcrumbContextProvider';
import IntlMessages from '../../../../../domain/utility/IntlMessages';
import { Card, Skeleton, Row, Col, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import CreateSpecificationAttributesHeader from './create-specification-attributes-header';
import { popupConfirm } from '../../../../../shared/components/popup-confirm';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import AttributeInfo from './attribute-info';
import { CreateSpecificationAttributeDto } from '../../../../../shared/services/specification-attribute/dto/create-specification-attribute-dto';
import { CategoryDto } from '../../../../../shared/services/categories/dtos/category-dto';
import { categoriesService } from '../../../../../shared/services/categories';
import { convertToTreeCascader } from '../../../../../shared/functions/convert-to-tree-cascader';
import { stockAdjustmentSchema } from './schema';
import { specificationAttributeService } from '../../../../../shared/services/specification-attribute';
import { SpecificationAttributeDetailsDto } from '../../../../../shared/services/specification-attribute/dto/specification-attribute-details-dto';
import AttributeOptions from './attribute-options';
import MainUtils from 'src/shared/utils/main';
import { useLocaleContext } from 'src/domain/utility/AppContextProvider/LocaleContextProvide';
import { getLocaleValue } from 'src/shared/functions/change-locales';
import LanguageCode from 'src/shared/services/types/language-code';

interface ICreateSpecificationAttributesProps { }

const CreateSpecificationAttributes = (
  props: ICreateSpecificationAttributesProps,
) => {
  const { setBreadcrumb }: any = useBreadcrumbContext();

  const { messages } = useIntl();

  const { id } = useParams();

  const navigate = useNavigate();

  const { locale }: any = useLocaleContext();

  const [
    isSpecificationAttributeDetailsLoading,
    setIsSpecificationAttributeDetailsLoading,
  ] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState<boolean>(false);

  const [initialValuesForm, setInitialValuesForm] =
    useState<CreateSpecificationAttributeDto>({
      name: '',
      selectedCategoryIds: [],
      isAvailableForAllCategories: false,
      locales: [],
    });

  const [categoriesList, setCategoriesList] = useState<CategoryDto[]>([]);

  const [specificationAttributeDetails, setSpecificationAttributeDetails] =
    useState<SpecificationAttributeDetailsDto>();

  const [isAvailableForCategories, setIsAvailableAllCategories] =
    useState(false);

  useEffect(() => {
    if (!MainUtils.isEmptyValue(id)) {
      (async () => {
        try {
          setIsSpecificationAttributeDetailsLoading(true);
          let productTagDetails =
            await specificationAttributeService.getSpecificationAttributeByID(
              id ?? '',
            );
          setSpecificationAttributeDetails(productTagDetails);
        } catch (error) {
          console.error(error);
        } finally {
          setIsSpecificationAttributeDetailsLoading(false);
        }
      })();
    }
  }, [id]);

  useEffect(() => {
    setBreadcrumb([
      {
        text: <IntlMessages id='Catalog.sideBarName' />,
        url: '/catalog/products',
      },
      {
        text: <IntlMessages id='catalog.attributes' />,
        url: '/catalog/catalog.attributes/product-attributes',
      },
      {
        text: <IntlMessages id='catalog.specificationAttributes' />,
        url: '/catalog/catalog.attributes/specification-attributes',
      },
      id
        ? {
          text: (
            <IntlMessages id='specificationAttributes.editSpecificationAttributes' />
          ),
          url: `/catalog/catalog.attributes/specification-attributes/edit-specification-attributes/${id}`,
        }
        : {
          text: (
            <IntlMessages id='specificationAttributes.createSpecificationAttributes' />
          ),
          url: '/catalog/catalog.attributes/specification-attributes/create-specification-attributes',
        },
    ]);
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        setCategoryLoading(true);
        let categories = await categoriesService.getAll();
        setCategoriesList(
          convertToTreeCascader(categories, undefined, locale.code),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setCategoryLoading(false);
      }
    })();
  }, [locale]);

  const itemsTab: TabsProps['items'] = !id
    ? [
      {
        key: 'attributeInfo',
        label: `${messages[
          'specificationAttributes.specificationAttributesInfo.standard'
        ].toString()}`,
        children: (
          <AttributeInfo
            categoriesList={categoriesList}
            setIsAvailableAllCategories={setIsAvailableAllCategories}
          />
        ),
      },
    ]
    : [
      {
        key: 'attributeInfo',
        label: `${messages[
          'specificationAttributes.specificationAttributesInfo.standard'
        ].toString()}`,
        children: (
          <AttributeInfo
            categoriesList={categoriesList}
            categoryLoading={categoryLoading}
            setIsAvailableAllCategories={setIsAvailableAllCategories}
          />
        ),
      },
      {
        key: 'options',
        label: `${messages[
          'specificationAttributes.specificationAttributesInfo.options'
        ].toString()}`,
        children: <AttributeOptions />,
      },
    ];

  useEffect(() => {
    if (specificationAttributeDetails)
      setInitialValuesForm({
        name: specificationAttributeDetails?.Name,
        id: specificationAttributeDetails?.Id,
        seName: specificationAttributeDetails?.SeName,
        isAvailableForAllCategories:
          specificationAttributeDetails?.IsAvailableForAllCategories,
        selectedCategoryIds: specificationAttributeDetails?.SelectedCategoryIds,
        locales:
          specificationAttributeDetails?.Locales?.map((item) => {
            return {
              id: item.Id,
              languageId: item.LanguageId,
              localeKey: item.LocaleKey,
              localeValue: item.LocaleValue,
            };
          }) ?? [],
      });
    else
      setInitialValuesForm({
        name: '',
        selectedCategoryIds: [],
        isAvailableForAllCategories: false,
        locales: [],
      });
  }, [specificationAttributeDetails]);

  const deleteSpecificationAttribute = () => {
    popupConfirm(async () => {
      try {
        let checlDelete =
          await specificationAttributeService.deleteSpecificationAttribute(
            specificationAttributeDetails?.Id ?? '',
          );
        if (!Object.prototype.hasOwnProperty.call(checlDelete, 'error'))
          navigate('/catalog/catalog.attributes/specification-attributes');
      } catch (error) {
        console.error(error);
      }
    }, `${messages['commom.areYouSureYouWantToDelete'].toString()}` + ' ' + [specificationAttributeDetails?.Name] + `?`);
  };

  const handleSubmit = async (values: any, setSubmitting: any) => {
    try {
      let checkCreateOrEdit: any;
      if (!id)
        checkCreateOrEdit = await specificationAttributeService.create({
          ...values,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
        });
      else {
        checkCreateOrEdit = await specificationAttributeService.edit({
          ...values,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
        });
      }
      if (!Object.prototype.hasOwnProperty.call(checkCreateOrEdit, 'error'))
        navigate('/catalog/catalog.attributes/specification-attributes');
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Formik
        enableReinitialize
        initialValues={initialValuesForm}
        validationSchema={
          isAvailableForCategories ? null : stockAdjustmentSchema(messages)
        }
        onSubmit={(values, { setSubmitting }: any) =>
          handleSubmit(values, setSubmitting)
        }
        validateOnChange={false}
        validateOnBlur={false}>
        {({ handleSubmit, isSubmitting }: any) => (
          <Form>
            <Skeleton active loading={isSpecificationAttributeDetailsLoading}>
              <Row gutter={[0, 48]}>
                <Col span={24}>
                  <CreateSpecificationAttributesHeader
                    deleteSpecificationAttribute={deleteSpecificationAttribute}
                    specificationAttributeDetails={
                      specificationAttributeDetails
                    }
                    isSubmitting={isSubmitting}
                    handleSubmit={handleSubmit}
                  />
                </Col>

                <Col span={24}>
                  <Tabs defaultActiveKey='attributeInfo' items={itemsTab} />
                </Col>
              </Row>
            </Skeleton>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export default CreateSpecificationAttributes;
